import styled from "@emotion/styled";
import { ellipsis } from "polished";
import * as React from "react";

import { Avatar, Button, DesignToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import { AlertSuccess, AlertWarning } from "components/alert-box";
import { Icon, IconsEnum } from "components/icon/Icon";
import { RoleTag } from "components/role-tag/RoleTag";
import { Section } from "components/section";
import { IAgent } from "store/entities/agents";
import { ITeam } from "store/entities/teams";
import { withVar } from "themes/helpers";

interface IProps {
  agent: IAgent;
  teams: ITeam[];
  canReactivateEmail: boolean;
}

interface IState {
  reactivatedEmail: boolean;
  reactivatingEmail: boolean;
  expandedTeamsList: boolean;
}

const TEAM_LIST_BREAKPOINT = 3;

export default class AgentsDetails extends React.PureComponent<IProps, IState> {
  state = {
    reactivatedEmail: false,
    reactivatingEmail: false,
    expandedTeamsList: false,
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    // Reset expanded list of teams
    if (nextProps.agent.id !== this.props.agent.id) {
      this.setState({
        reactivatedEmail: false,
        reactivatingEmail: false,
        expandedTeamsList: false,
      });
    }
  }

  private handleReactivateEmail = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    this.setState({ reactivatingEmail: true });
  };

  private handleToggleTeamList = (): void => {
    this.setState(prevState => ({
      expandedTeamsList: !prevState.expandedTeamsList,
    }));
  };

  render(): JSX.Element {
    const { agent } = this.props;
    const teams = this.state.expandedTeamsList ? this.props.teams : this.props.teams.slice(0, TEAM_LIST_BREAKPOINT);
    const hiddenTeamsCount = this.props.teams.length - TEAM_LIST_BREAKPOINT;

    return (
      <StyledDetails>
        <GeneralInfo>
          <StyledAvatar>
            <Avatar
              type={agent.avatar ? "image" : "text"}
              src={agent.avatar ? agent.avatar : undefined}
              text={agent.name ?? agent.email}
              size="xxlarge"
            />
          </StyledAvatar>
          <AgentInfoWrapper>
            <AgentNameWrapper>
              <AgentName>{agent.name}</AgentName>
              <RoleTag role={agent.permission} />
            </AgentNameWrapper>
            <AgentEmail size="sm">{agent.email}</AgentEmail>
          </AgentInfoWrapper>
        </GeneralInfo>

        {agent.emailBounced && (
          <AlertWarning>
            <div>
              <Icon
                name={IconsEnum.Warning}
                inline
                width="17px"
                height="14px"
                fill={withVar(DesignToken.ContentBasicWarning)}
              />{" "}
              We couldn’t deliver emails to this agent{" "}
              {agent.emailBounced.date && (
                <>
                  {" "}
                  since <strong>{agent.emailBounced.date.toString()}</strong>
                </>
              )}
              .
            </div>
            <br />
            <div>
              Please check the email for typos, make sure it has storage space, etc.{" "}
              {this.props.canReactivateEmail ? (
                <>
                  Once done, you can try to{" "}
                  <ReactivateEmail href="" data-navigate="0" onClick={this.handleReactivateEmail}>
                    {this.state.reactivatingEmail ? "reactivating.." : "reactivate it"}
                  </ReactivateEmail>
                  .
                </>
              ) : (
                <>Please notify the Admin to reactivate it in the agent&apos;s profile.</>
              )}
            </div>
          </AlertWarning>
        )}

        {!agent.emailBounced && this.state.reactivatedEmail && (
          <AlertSuccess>
            <span>
              <Icon
                name={IconsEnum.RoundFilledCheckMark}
                fill={withVar(DesignToken.SurfaceAccentEmphasisHighPositive)}
                inline
                width="20px"
                height="14px"
              />{" "}
              Email has been reactivated
            </span>
          </AlertSuccess>
        )}

        <Section title="Teams">
          <StyledContent data-testid="teams-member">
            {teams.map(team => (
              <StyledTeamDetails key={team.id} data-testid="team-details">
                <Avatar type="text" text={team.name} shape="rounded-square" size="xxsmall" css={{ minWidth: "20px" }} />
                <TeamName size="sm" data-testid="team-name">
                  {team.name}
                </TeamName>
              </StyledTeamDetails>
            ))}
            {this.props.teams.length > TEAM_LIST_BREAKPOINT && (
              <Button kind="link" onClick={this.handleToggleTeamList} type="button">
                {this.state.expandedTeamsList ? `Hide ${hiddenTeamsCount}...` : `Show ${hiddenTeamsCount} more...`}
              </Button>
            )}
          </StyledContent>
        </Section>
      </StyledDetails>
    );
  }
}

const StyledDetails = styled.div`
  color: ${withVar(DesignToken.ContentBasicPrimary)};
`;

const TeamName = styled(Text)`
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  ${ellipsis()};
`;

const StyledTeamDetails = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing2)};
  max-width: 100%;
`;

const GeneralInfo = styled.div`
  display: flex;
  overflow: hidden;
  margin-bottom: 16px;
`;

const StyledAvatar = styled.div`
  flex: 0 0 auto;
  margin-right: 18px;
`;

const AgentInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;

  .name {
    font-weight: bold;
    vertical-align: middle;
  }

  .label {
    display: inline-block;
    margin-left: 8px;
    vertical-align: middle;
  }
`;

const StyledContent = styled.div`
  padding: var(${SpacingToken.Spacing3}) var(${SpacingToken.Spacing2}) var(${SpacingToken.Spacing8});
  display: flex;
  flex-direction: column;
  gap: ${withVar(SpacingToken.Spacing3)};
  align-items: flex-start;
`;

const ReactivateEmail = styled.a`
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  font-weight: 600;
  text-decoration: underline;
`;

const AgentNameWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(${SpacingToken.Spacing1});
`;

const AgentName = styled(Text)`
  word-break: break-word;
`;

const AgentEmail = styled(Text)`
  color: ${withVar(DesignToken.ContentBasicSecondary)};
  word-break: break-word;
`;
